import React, { useState, useRef, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Sparkles, X, Send, Moon } from "lucide-react";

const STARTERS = [
  "Is this halal, and why?",
  "What purification do I owe?",
  "Explain the debt ratio screen",
];

// Just a random conversation id so the backend can thread replies — nothing sensitive.
const sessionKey = "tharwy-assistant-session";
const newSession = () => {
  let id = sessionStorage.getItem(sessionKey);
  if (!id) { id = `ask-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; sessionStorage.setItem(sessionKey, id); }
  return id;
};

/** Floating Shariah assistant. Knows which instrument page you're on. */
export const AskTharwy = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const location = useLocation();
  const scroller = useRef(null);
  const sessionId = useRef(newSession());

  const symbol = location.pathname.startsWith("/stock/")
    ? decodeURIComponent(location.pathname.split("/stock/")[1].split("/")[0])
    : null;

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const question = (text ?? input).trim();
    if (!question || streaming) return;
    setInput("");
    const turn = Date.now();
    setMessages((m) => [...m, { id: `u-${turn}`, role: "user", content: question },
                        { id: `a-${turn}`, role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assistant/ask`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: question, session_id: sessionId.current, symbol }),
      });
      if (!res.ok || !res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          next[next.length - 1] = { ...last, content: last.content + chunk };
          return next;
        });
      }
    } catch (e) {
      console.error("Assistant stream failed:", e);
      setMessages((m) => {
        const next = [...m];
        const last = next[next.length - 1];
        next[next.length - 1] = {
          ...last,
          content: (last.content ? last.content + "\n\n" : "")
            + "I lost the connection there. Please ask me again.",
        };
        return next;
      });
    } finally { setStreaming(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="ask-tharwy-button"
        className="fixed bottom-6 end-6 z-50 flex items-center gap-2 rounded-full bg-[color:var(--tharwy-indigo)] px-5 py-3.5 text-sm font-semibold text-white shadow-xl transition-transform hover:scale-105">
        <Sparkles className="h-4 w-4" />Ask Tharwy
      </button>
    );
  }

  return (
    <Card data-testid="ask-tharwy-panel"
      className="fixed bottom-6 end-6 z-50 flex h-[560px] w-[min(92vw,420px)] flex-col overflow-hidden shadow-2xl animate-fade-up">
      <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Moon className="h-4 w-4 text-[color:var(--tharwy-indigo)]" />
          <div>
            <div className="text-sm font-semibold leading-tight">Ask Tharwy</div>
            <div className="text-[11px] text-muted-foreground leading-tight">
              {symbol ? `Looking at ${symbol}` : "Shariah screening, explained"}
            </div>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setOpen(false)} data-testid="ask-tharwy-close">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div ref={scroller} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="ask-tharwy-messages">
        {!messages.length && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground leading-relaxed">
              I read Tharwy's own AAOIFI screening. Ask me whether something is halal, why it passed
              or failed, and what purification you owe. I can't give investment advice.
            </p>
            <div className="flex flex-wrap gap-2">
              {STARTERS.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid={`ask-tharwy-starter-${s.slice(0, 8)}`}
                  className="rounded-full border px-3 py-1.5 text-xs transition-colors hover:bg-secondary">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={m.id} className={m.role === "user" ? "flex justify-end" : ""}
            data-testid={`ask-tharwy-msg-${m.role}-${i}`}>
            <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
              m.role === "user" ? "bg-[color:var(--tharwy-indigo)] text-white" : "bg-secondary"}`}>
              {m.content || (streaming ? "Thinking..." : "")}
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 border-t p-3">
        <Input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={symbol ? `Ask about ${symbol}...` : "Ask about any stock or ETF..."}
          data-testid="ask-tharwy-input" />
        <Button onClick={() => send()} disabled={streaming || !input.trim()}
          size="icon" className="shrink-0" data-testid="ask-tharwy-send">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
};
